import React, { ReactNode, useContext, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { Outlet, useLocation } from 'react-router'

import { Box, CssBaseline, Paper as MuiPaper, Theme, useMediaQuery, useTheme } from '@mui/material'
import { spacing } from '@mui/system'

import GlobalStyle from '../components/GlobalStyle'
import Navbar from '../components/navbar/Navbar'
import Sidebar from '../components/sidebar/Sidebar'
import Footer from '../components/Footer'
import { useTranslation } from 'react-i18next'
import { translateObjectDeep } from '../utils/i18n'
import { SidebarNavProps } from '../components/sidebar/SidebarNav'
import { LayoutContext } from '../contexts/LayoutContext'
import { GlobalLoader } from '../components/GlobalLoader'

interface PropsWithTheme {
    theme?: Theme
}

export const NAVBAR_DRAWER_WIDTH = 215

const NavbarWrapper = styled.div`
    grid-area: navbar;
    position: sticky;
    top: 0;
    z-index: 101;
`

const Root = styled.div`
    min-height: 100vh;
    display: grid;
    grid-template:
        'announcment' fit-content(30px)
        'navbar' fit-content(64px)
        'app-content' auto / auto;

    .navbar-wrapper {
        max-width: 100vw;
    }

    ${(props) => props.theme.breakpoints.up('md')} {
        grid-template:
            'sidebar announcment' fit-content(30px)
            'sidebar navbar' fit-content(64px)
            'sidebar app-content' auto / 215px ${(props) =>
                props.style?.width === 'max-content' ? 'auto' : 'calc(100vw - 230px)'};

        .navbar-wrapper {
            left: ${NAVBAR_DRAWER_WIDTH}px;
            max-width: calc(100vw - ${NAVBAR_DRAWER_WIDTH}px);
        }
    }
`

const Drawer = styled.div<PropsWithTheme>`
    ${(props) => props.theme.breakpoints.up('md')} {
        width: ${NAVBAR_DRAWER_WIDTH}px;
        flex-shrink: 0;
        grid-area: sidebar;
    }
`

const AppContent = styled.div<{ contentWidth: string }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    max-width: 100vw;
    ${(props) => props.theme.breakpoints.up('md')} {
        max-width: ${(props) => (props.contentWidth ? props.contentWidth : '100vw')};
    }
    grid-area: app-content;
`

const Paper = styled(MuiPaper)(spacing)

const MainContent = styled(Paper)<PropsWithTheme>`
    flex: 1;
    background: ${(props) => props.theme.palette.background.default};

    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
        flex: none;
    }

    .MuiPaper-root .MuiPaper-root {
        box-shadow: none;
    }
`

export interface DashboardBaseProps {
    children: ReactNode
    dashboardItems: Array<
        SidebarNavProps['items'][number] & {
            titleI18n: string
            pages: Array<SidebarNavProps['items'][number]['pages'][number] & { titleI18n: string }>
        }
    >
}

export const DashboardBase = ({ children, dashboardItems }: DashboardBaseProps) => {
    const router = useLocation()
    const [mobileOpen, setMobileOpen] = useState(false)
    const { t } = useTranslation()
    const { pageWidth } = useContext(LayoutContext)

    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen)
    }

    // Close mobile menu when navigation occurs
    useEffect(() => {
        setMobileOpen(false)
    }, [router.pathname])

    translateObjectDeep(dashboardItems, t)

    return (
        <Root style={{ width: pageWidth }}>
            <CssBaseline />
            <GlobalStyle />
            <Drawer>
                <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                    <Sidebar
                        variant='temporary'
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        items={dashboardItems}
                        slotProps={{
                            paper: {
                                style: { width: NAVBAR_DRAWER_WIDTH },
                            },
                        }}
                    />
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Sidebar slotProps={{ paper: { style: { width: NAVBAR_DRAWER_WIDTH } } }} items={dashboardItems} />
                </Box>
            </Drawer>

            <NavbarWrapper className='navbar-wrapper'>
                <GlobalLoader />
                <Navbar onDrawerToggle={handleDrawerToggle} />
            </NavbarWrapper>

            <AppContent contentWidth={pageWidth}>
                <MainContent p={isSmallScreen ? '24px 12px' : 6}>
                    {children}
                    <Outlet />
                </MainContent>
                <Footer />
            </AppContent>
        </Root>
    )
}
